import React, { useState, useEffect } from "react";
import emailjs from 'emailjs-com';
import { useLocation } from "react-router-dom"; // Import for capturing URL parameters

const Form = () => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    subject: "", // Added subject to formData state
    description: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [agency, setAgency] = useState(""); // State to hold the agency parameter

  const location = useLocation();

  // Capture agency parameter from URL
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const agencyParam = searchParams.get("agency") || "unknown";
    setAgency(agencyParam);
  }, [location.search]);

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;

    // Restrict phone input to numbers only
    if (name === "phone" && !/^[0-9]*$/.test(value)) {
      return; // If the value contains non-numeric characters, do nothing
    }

    setFormData({ ...formData, [name]: value });
  };

  // Form validation
  const validate = () => {
    const newErrors = {};

    // Name validation
    if (!formData.name.trim()) newErrors.name = "Name is required.";

    // Phone validation (must be an 11-digit number)
    const phonePattern = /^[0-9]{11}$/;
    if (!formData.phone.trim() || !phonePattern.test(formData.phone))
      newErrors.phone = "Phone must be a valid 11-digit number.";

    // Email validation (must contain "@" and follow a valid format)
    const emailPattern = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
    if (!formData.email.trim()) {
      newErrors.email = "Email is required.";
    } else if (!formData.email.includes("@") || !emailPattern.test(formData.email)) {
      newErrors.email = "Enter a valid email address containing '@'.";
    }

    // Subject validation
    if (!formData.subject.trim()) newErrors.subject = "Subject is required.";

    // Set errors
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate form before sending
    if (validate()) {
      // Use EmailJS to send the email
      emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,     // Use environment variable for service ID
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,    // Use environment variable for template ID
        {
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          subject: formData.subject, // Include the subject parameter
          description: formData.description,
          agency: agency, // Include the agency parameter
        },
        process.env.REACT_APP_EMAILJS_USER_ID // Use environment variable for user ID
      )
      .then((response) => {
        console.log('SUCCESS!', response.status, response.text);
        setIsSubmitted(true);  // Show success message
        alert("Form submitted successfully!");

        // Clear the form data
        setFormData({
          name: "",
          phone: "",
          email: "",
          subject: "",
          description: "",
        });

        // Clear the error messages
        setErrors({});
      })
      .catch((err) => {
        console.log('FAILED...', err);
        alert("There was an error submitting the form. Please try again.");
      });
    }
  };

  return (
    <div style={styles.formContainer}>
      {isSubmitted ? (
        <div>
          <h2>Form Submitted!</h2>
          <p>Thank you for contacting Scorpion Capital</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} style={styles.form}>
          <h2 style={styles.heading}>New Client Form</h2>

          {/* Name field */}npm
          <div style={styles.formGroup}>
            <label htmlFor="name">Name <span style={styles.requiredAsterisk}>*</span></label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              style={styles.input}
            />
            {errors.name && <p style={styles.error}>{errors.name}</p>}
          </div>

          {/* Phone field (restricted to numbers only) */}
          <div style={styles.formGroup}>
            <label htmlFor="phone">Phone <span style={styles.requiredAsterisk}>*</span></label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              maxLength="11"
              placeholder=""
              style={styles.input}
            />
            {errors.phone && <p style={styles.error}>{errors.phone}</p>}
          </div>

          {/* Email field */}
          <div style={styles.formGroup}>
            <label htmlFor="email">Email <span style={styles.requiredAsterisk}>*</span></label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              style={styles.input}
            />
            {errors.email && <p style={styles.error}>{errors.email}</p>}
          </div>

          {/* Subject dropdown */}
          <div style={styles.formGroup}>
            <label htmlFor="subject">Subject <span style={styles.requiredAsterisk}>*</span></label>
            <select
              id="subject"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              style={styles.input}
            >
              <option value="">Select a subject</option>
              <option value="Bridging Loans">Bridging Loans</option>
              <option value="Commercial Mortgages">Commercial Mortgages</option>
              <option value="Term Loans">Term Loans</option>
              <option value="Mezzanine Lending">Mezzanine Lending</option>
              <option value="Development Finance">Development Finance</option>
              <option value="Development Exit Finance">Development Exit Finance</option>
              <option value="Investment Finance">Investment Finance</option>
              <option value="Portfolio Restructuring & Formation">Portfolio Restructuring & Formation</option>
              <option value="Auction Finance">Auction Finance</option>
            </select>
            {errors.subject && <p style={styles.error}>{errors.subject}</p>}
          </div>

          {/* Description field */}
          <div style={styles.formGroup}>
            <label htmlFor="description">Description (optional)</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              style={styles.textarea}
            />
          </div>

          {/* Submit button */}
          <button type="submit" style={styles.button}>Submit</button>
        </form>
      )}
    </div>
  );
};

const styles = {
  formContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100vh",
    backgroundColor: "#fff",
    padding: "20px",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    padding: "20px",
    border: "1px solid rgba(0, 0, 0, 0.1)", // Lighter border color
    borderRadius: "10px",
    backgroundColor: "#fff",
    maxWidth: "600px", // Adjusted to make the form wider
    width: "100%",
  },
  heading: {
    textAlign: "center", // Center the heading
  },
  logo: {
    width: "150px",
    marginBottom: "20px",
    alignSelf: "center",
  },
  formGroup: {
    marginBottom: "15px",
  },
  input: {
    padding: "10px",
    fontSize: "16px",
    width: "100%",
    boxSizing: "border-box",
    border: "1px solid rgba(0, 0, 0, 0.3)", // Lighter border color for input fields
  },
  textarea: {
    padding: "10px",
    fontSize: "16px",
    width: "100%",
    height: "80px",
    boxSizing: "border-box",
    border: "1px solid rgba(0, 0, 0, 0.3)", // Lighter border color for textarea
  },
  button: {
    padding: "10px",
    fontSize: "16px",
    backgroundColor: "#dcc181",
    color: "#fff",
    border: "none",
    cursor: "pointer",
    borderRadius: "5px",
  },
  error: {
    color: "red",
    fontSize: "0.875em",
  },
  requiredAsterisk: {
    color: "red",
    marginLeft: "5px",
  },
};

export default Form;